import { useEffect } from "react";
import { ArrowLeft, ArrowUpRight, MapPin } from "lucide-react";
import { ADULT_COURSES, COURSES, LOCATION, SITE, bookingHref } from "../../content";
import { Eyebrow, Reveal } from "./Primitives";
import { Seo } from "./Seo";

const Course = ({ c }) => (
  <li data-testid={`dieburg-course-${c.slug}`} className="grid gap-4 border-t border-navy/10 py-8 sm:grid-cols-12 sm:items-start">
    <div className="sm:col-span-4">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-sky-700">{c.kicker}</p>
      <h3 className="mt-2 text-2xl font-extrabold tracking-tight text-navy">{c.name}</h3>
      <p className="mt-1 text-sm font-semibold text-slate-600">{c.age}</p>
    </div>
    <p className="text-base leading-relaxed text-slate-600 sm:col-span-5">{c.text}</p>
    <div className="flex gap-5 text-sm font-bold sm:col-span-3 sm:justify-end">
      <a href={`/#kurse`} className="text-navy underline-offset-4 hover:underline">Lernziele</a>
      <a href={bookingHref(c)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-navy underline-offset-4 hover:underline">
        Jetzt buchen <ArrowUpRight className="h-4 w-4" />
      </a>
    </div>
  </li>
);

export default function DieburgPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <main id="main" data-testid="dieburg-page" className="bg-white">
      <Seo
        title="Schwimmkurse Dieburg im Sportbad | Schwimmschule Manta"
        description="Schwimmkurse in Dieburg für Kinder aus Dieburg, Darmstadt und dem Landkreis Darmstadt-Dieburg: Froschkurs, Seepferdchen, Seeräuber & Bronze im Sportbad Dieburg."
        path="/schwimmkurse-dieburg"
      />
      <section className="bg-navy pb-20 pt-36 text-white sm:pt-44">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12">
          <a href="/" className="group inline-flex items-center gap-2 text-sm font-semibold text-sky-200 hover:text-white">
            <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" /> Zur Startseite
          </a>
          <Eyebrow dark className="mt-10">Schwimmschule Dieburg</Eyebrow>
          <h1 className="mt-6 max-w-4xl text-4xl font-extrabold leading-[1.05] tracking-[-0.02em] sm:text-5xl lg:text-6xl">
            Schwimmkurse in Dieburg <span className="font-light italic text-sky-200">im Sportbad Dieburg.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-slate-200 md:text-lg">
            Schwimmschule Manta bietet Schwimmkurse für Kinder im Sportbad Dieburg an – vom ersten Wasserkontakt im Froschkurs über den Seepferdchenkurs bis zum Schwimmabzeichen in Bronze. Unsere Kurse richten sich an Familien aus Dieburg, Darmstadt und dem Landkreis Darmstadt-Dieburg.
          </p>
          <a href="/#buchung" data-testid="dieburg-book-btn" className="mt-10 inline-flex items-center gap-2 rounded-full bg-white px-7 py-4 text-sm font-bold text-navy transition-colors hover:bg-sky-100">
            Kursplatz sichern <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-5 py-20 sm:px-8 sm:py-28 lg:px-12">
        <Reveal>
          <Eyebrow>Kinderschwimmkurse</Eyebrow>
          <h2 className="mt-6 text-3xl font-extrabold tracking-[-0.02em] text-navy sm:text-4xl">Unsere Schwimmkurse für Kinder in Dieburg</h2>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-600">
            Die Kurse bauen aufeinander auf. Wo Ihr Kind einsteigt, richtet sich nach seinem aktuellen Abzeichen – der Schwimmkurs für Anfänger ist der Froschkurs.
          </p>
        </Reveal>
        <ul className="mt-10 border-b border-navy/10">
          {COURSES.map((c) => <Course key={c.slug} c={c} />)}
        </ul>

        <div className="mt-20 grid gap-12 lg:grid-cols-2">
          <Reveal>
            <h2 className="text-3xl font-extrabold tracking-[-0.02em] text-navy">Schwimmkurse für Erwachsene &amp; Privatstunden</h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              Auch Erwachsene lernen bei uns schwimmen: im {ADULT_COURSES.map((a) => a.name).join(" und im ")}. Für individuelle Förderung bieten wir außerdem Privatstunden für jedes Alter und Schwimmniveau an.
            </p>
            <div className="mt-6 flex flex-wrap gap-5 text-sm font-bold text-navy">
              <a href="/#erwachsene" className="underline-offset-4 hover:underline">Kurse für Erwachsene</a>
              <a href="/#privatstunden" className="underline-offset-4 hover:underline">Privatstunden</a>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="border border-navy/10 bg-ice p-8 sm:p-10">
              <h2 className="flex items-center gap-3 text-2xl font-extrabold text-navy">
                <MapPin className="h-6 w-6 text-sky-700" strokeWidth={1.5} /> {LOCATION.pool}
              </h2>
              <address className="mt-5 text-lg not-italic leading-relaxed text-navy">
                {LOCATION.street}
                <br />
                {LOCATION.zip}
              </address>
              <a href={LOCATION.mapsUrl} target="_blank" rel="noopener noreferrer" className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-navy hover:underline">
                Route zum Sportbad Dieburg planen <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </Reveal>
        </div>

        <Reveal className="mt-20 border-t border-navy/10 pt-12">
          <h2 className="text-3xl font-extrabold tracking-[-0.02em] text-navy">Fragen zum Schwimmkurs in Dieburg?</h2>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-600">
            Antworten zu Alter, Seepferdchen, Seeräuber und Bronze finden Sie in unseren <a href="/#faq" className="font-semibold text-navy underline underline-offset-4">häufigen Fragen</a>. Oder schreiben Sie uns an{" "}
            <a href={`mailto:${SITE.email}`} className="font-semibold text-navy underline underline-offset-4">{SITE.email}</a>.
          </p>
        </Reveal>
      </section>
    </main>
  );
}
